import * as React from 'react';
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import type { GoogleMapMarkerProps } from '../GoogleMap.types';
import styles from './GoogleMapMarker.module.scss';
import {
  getGoogleMapsDirectionsUrl,
  getLinkInfo,
  isValidLocationParam,
} from './utils/utils';
import { PIN_ICON_HEIGHT, TestIds } from '../constants';

const GoogleMapMarker = ({
  location,
  infoWindowOpen,
  onMarkerClick,
  onInfoWindowClose,
  showDirectionLink,
  allyTargetBlankDescription,
}: GoogleMapMarkerProps) => {
  const directionsUrl = getGoogleMapsDirectionsUrl(location);
  const { isLinkValid, anchorProps } = getLinkInfo(location);

  const handleMarkerClick = () => {
    onMarkerClick?.({
      type: 'markerClicked',
      address: location.address,
      location: {
        latitude: location.latitude || 0,
        longitude: location.longitude || 0,
      },
      icon: location.pinIcon || '',
      link: location.locationLinkAttributes?.href,
      title: location.title,
      linkTitle: location.linkTitle,
      description: location.description,
    });
  };

  if (
    !location ||
    !isValidLocationParam(location.longitude) ||
    !isValidLocationParam(location.latitude)
  ) {
    return null;
  }

  const position = {
    lat: Number(location.latitude),
    lng: Number(location.longitude),
  };

  const iconSvg = location.pinIcon && (
    <div
      aria-hidden={true}
      dangerouslySetInnerHTML={{ __html: location.pinIcon }}
      data-testid={TestIds.markerSvg}
      className={styles.pinIcon}
    />
  );

  const infoWindowContent = (
    <div>
      {location.description && (
        <p className={styles.description}>{location.description}</p>
      )}
      {location.linkTitle && isLinkValid && (
        <a
          className={styles.link}
          {...anchorProps}
          data-testid={TestIds.markerLink}
        >
          {location.linkTitle}
          {location.locationLinkAttributes?.target === '_blank' &&
            allyTargetBlankDescription && (
              <span className={styles.srOnly}>
                {allyTargetBlankDescription}
              </span>
            )}
        </a>
      )}
      {showDirectionLink && directionsUrl && (
        <a
          href={directionsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
          data-testid={TestIds.markerDirectionsLink}
          aria-label={`${location.directionTitle}. ${allyTargetBlankDescription}`}
        >
          {location.directionTitle}
        </a>
      )}
    </div>
  );

  return (
    <>
      <AdvancedMarker
        position={position}
        title={location.title}
        onClick={handleMarkerClick}
      >
        {iconSvg}
      </AdvancedMarker>
      {infoWindowOpen && (
        <InfoWindow
          position={position}
          pixelOffset={[0, -PIN_ICON_HEIGHT]}
          shouldFocus={false}
          maxWidth={300}
          minWidth={228}
          headerContent={
            location.title && <h2 className={styles.title}>{location.title}</h2>
          }
          onCloseClick={onInfoWindowClose}
          disableAutoPan
        >
          {infoWindowContent}
        </InfoWindow>
      )}
    </>
  );
};

export default React.memo(GoogleMapMarker);
